<template>
  <div ref="rootRef">
    <div v-if="modelValue?.length" class="selected-list">
      <div
        v-for="(usageItem, index) in modelValue"
        :key="`${usageItem.type}-${usageItem.typeCounter}-${index}`"
        class="item-card"
        :class="{
          'is-drop-target-before':
            dragOverIndex === index && draggingIndex !== null && draggingIndex !== index && draggingIndex > index,
          'is-drop-target-after':
            dragOverIndex === index && draggingIndex !== null && draggingIndex !== index && draggingIndex < index,
          'is-dragging': draggingIndex === index,
          'is-editing': editingIndex === index,
        }"
        draggable="true"
        @dragstart="onDragStart(index, $event)"
        @dragend="onDragEnd"
        @dragover.prevent="onDragOver(index)"
        @drop.prevent="onDrop(index)">
        <div class="item-row">
          <div class="item-main">
            <span class="drag-handle" title="Reihenfolge ändern">
              <IconGripVertical class="h-4 w-4" stroke="1.8" />
            </span>
            <div class="min-w-0">
              <div class="flex min-w-0 items-center gap-1.5">
                <span class="truncate">{{ getCardTitle(usageItem, index) }}</span>
                <span
                  v-if="getCategoryReferenceTooltip(usageItem, index)"
                  v-tooltip="getCategoryReferenceTooltip(usageItem, index)"
                  class="category-reference-hint"
                  tabindex="0"
                  role="img"
                  :aria-label="wt('usages', 'categoryReference', 'title')"
                  data-testid="usage-category-reference-hint">
                  <IconAlertTriangle class="h-4 w-4" stroke="1.8" />
                </span>
              </div>
              <div v-if="getCardSubtitle(usageItem)" class="truncate text-xs text-neutral-550">
                {{ getCardSubtitle(usageItem) }}
              </div>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="action-button"
              :class="{ 'is-active': editingIndex === index }"
              title="Nutzung ändern"
              :aria-expanded="editingIndex === index ? 'true' : 'false'"
              aria-haspopup="menu"
              @click="toggleUsageMenu(index)">
              <IconEdit class="h-4 w-4" stroke="1.8" />
            </button>
            <button
              type="button"
              class="action-button remove-button"
              :title="$t('project.general.sections.usage_definition.usage.tip.remove')"
              @click="removeUsage(index)">
              <IconX class="h-5 w-5" stroke="1.8" />
            </button>
          </div>
        </div>

        <div v-if="editingIndex === index" class="change-menu" role="menu">
          <UsageHierarchyPicker
            :country="props.country"
            :allowedUsageTypes="props.allowedUsageTypes"
            :selectedUsageType="usageItem.type"
            :selectedBuildingTypeId="getSelectedBuildingTypeId(usageItem)"
            @select="changeUsageSelection(index, $event)" />
        </div>
      </div>
    </div>
    <div v-else class="border p-2 text-neutral-550">
      {{ $t('project.general.sections.usage_definition.usage.hint.init') }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, type PropType } from 'vue'
import { IconAlertTriangle, IconEdit, IconGripVertical, IconX } from '@tabler/icons-vue'
import { useI18n } from 'vue-i18n'
import { deepClone } from '@/helpers/objects_arrays'
import { getUsageDisplaySubtitle, getUsageDisplayTitle } from '@/helpers/usage-display'
import type { UsageItem, SharedUsageItem } from '@/schemas/general/types'
import { useWidgetText } from '@/helpers/useWidgetText'
import UsageHierarchyPicker from '@/components/partials/inputs/UsageHierarchyPicker.vue'
import { useContentStore } from '@/stores/content'
import { useCategoryReferenceHints, type UsageSet } from '@/helpers/useCategoryReferenceHints'
import { STANDARD_USAGE_TYPES } from '@/helpers/usage-types'
import { fillPlaceholders } from '@/helpers/text-placeholders'

const { wt } = useWidgetText('widget-texts-project-general')
const contentStore = useContentStore()
const emit = defineEmits<{
  (e: 'reorder', payload: { fromIndex: number; toIndex: number }): void
}>()
type UsageWithBuildingType = Omit<UsageItem | SharedUsageItem, 'type'> & {
  type: string
  buildingTypeId?: string | null
  buildingTypeName?: string | null
}
const modelValue = defineModel({ type: Array as PropType<UsageWithBuildingType[]> })
const props = withDefaults(
  defineProps<{ country?: string | null; allowedUsageTypes?: readonly string[]; usageSet?: UsageSet }>(),
  {
    allowedUsageTypes: () => STANDARD_USAGE_TYPES,
    usageSet: 'standard',
  }
)
const { locale } = useI18n()
const { wt: wtRoles } = useWidgetText('widget-texts-general')
const { hasMultipleRoles, rolesUsingCategoryReference } = useCategoryReferenceHints(() => props.usageSet)
const categoryReferenceTooltipTheme = { 'max-width': '460px' } as const
type UsagePickerSelection = {
  usageType: string
  buildingTypeId: string | null
  buildingTypeName: string | null
}
const editingIndex = ref<number | null>(null)
const rootRef = ref<HTMLElement | null>(null)
const draggingIndex = ref<number | null>(null)
const dragOverIndex = ref<number | null>(null)

function toggleUsageMenu(index: number): void {
  editingIndex.value = editingIndex.value === index ? null : index
}

function closeUsageMenu(): void {
  editingIndex.value = null
}

function changeUsageSelection(index: number, selection: UsagePickerSelection): void {
  if (!modelValue.value?.[index]) return

  const copiedUsageItems = deepClone(modelValue.value) as UsageWithBuildingType[]
  copiedUsageItems[index].type = selection.usageType
  copiedUsageItems[index].buildingTypeId = selection.buildingTypeId
  copiedUsageItems[index].buildingTypeName = selection.buildingTypeName
  updateTypeCounter(copiedUsageItems)
  modelValue.value = copiedUsageItems
  closeUsageMenu()
}

function getCardTitle(usageItem: UsageWithBuildingType, index: number): string {
  return getUsageDisplayTitle(usageItem, wt, {
    allUsages: (modelValue.value ?? []) as UsageWithBuildingType[],
    index,
    resolveBuildingTypeName: contentStore.getBuildingTypeNameById,
  })
}

/**
 * VAL-160: explains that a building type without own reference values is calculated
 * with those of its usage category. The role line only appears in multi-role projects.
 */
function getCategoryReferenceTooltip(usageItem: UsageWithBuildingType, index: number) {
  const roles = rolesUsingCategoryReference.value(index)
  // The texts come from Payload only; without a text there is nothing to explain.
  const template = wt('usages', 'categoryReference', 'text')
  if (!usageItem.buildingTypeId || !roles.length || !template) return null

  const buildingType =
    contentStore.getBuildingTypeNameById(usageItem.buildingTypeId) ?? usageItem.buildingTypeName ?? ''
  const text = fillPlaceholders(template, { buildingType, category: wt('usages', 'option', usageItem.type) })
  // "Architektur, Elektro und Sanitär": a readable list in the user's language.
  const roleNames = new Intl.ListFormat(locale.value, { type: 'conjunction' }).format(
    roles.map((role) => wtRoles('roleParts', role) || role)
  )
  const roleTemplate = wt('usages', 'categoryReference', roles.length === 1 ? 'roleSingle' : 'roleMultiple')
  const roleLine =
    hasMultipleRoles.value && roleTemplate ? `\n${fillPlaceholders(roleTemplate, { roles: roleNames })}` : ''
  return {
    title: wt('usages', 'categoryReference', 'title'),
    text: text + roleLine,
    portal: true,
    theme: categoryReferenceTooltipTheme,
  }
}

function getCardSubtitle(usageItem: UsageWithBuildingType): string {
  return getUsageDisplaySubtitle(usageItem, wt, contentStore.getBuildingTypeNameById)
}

function getSelectedBuildingTypeId(usageItem: UsageWithBuildingType): string | null {
  return usageItem.buildingTypeId ?? null
}

function onDocumentClick(event: MouseEvent): void {
  const target = event.target as Node | null
  if (!target || !rootRef.value?.contains(target)) {
    closeUsageMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
})

function removeUsage(index: number): void {
  if (!modelValue.value) return

  const copiedUsageItems = deepClone(modelValue.value)
  const removedRatio = copiedUsageItems[index]?.ratio ?? 0
  copiedUsageItems.splice(index, 1)
  if (editingIndex.value !== null) {
    if (editingIndex.value === index) {
      editingIndex.value = null
    } else if (editingIndex.value > index) {
      editingIndex.value -= 1
    }
  }

  if (!copiedUsageItems.length) {
    modelValue.value = copiedUsageItems
    return
  }

  // Transfer the removed share to the usage directly above it.
  // If the first item is removed, transfer to the new first item.
  const recipientIndex = index > 0 ? index - 1 : 0
  if (copiedUsageItems[recipientIndex]) {
    copiedUsageItems[recipientIndex].ratio += removedRatio
  }

  // Keep the total exactly at 100 to avoid floating-point drift.
  const ratioSum = copiedUsageItems.reduce((sum, usage) => sum + usage.ratio, 0)
  const correction = 100 - ratioSum
  if (copiedUsageItems[recipientIndex] && correction !== 0) {
    copiedUsageItems[recipientIndex].ratio += correction
  }

  updateTypeCounter(copiedUsageItems)
  modelValue.value = copiedUsageItems
}

function onDragStart(index: number, event: DragEvent): void {
  draggingIndex.value = index
  dragOverIndex.value = index
  closeUsageMenu()
  event.dataTransfer?.setData('text/plain', String(index))
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
  }
}

function onDragOver(index: number): void {
  if (draggingIndex.value === null) return
  dragOverIndex.value = index
}

function onDrop(targetIndex: number): void {
  const fromIndex = draggingIndex.value
  if (fromIndex === null) return

  dragOverIndex.value = null
  draggingIndex.value = null

  if (fromIndex === targetIndex || !modelValue.value) return

  const copiedUsageItems = deepClone(modelValue.value)
  const [movedItem] = copiedUsageItems.splice(fromIndex, 1)
  if (!movedItem) return
  copiedUsageItems.splice(targetIndex, 0, movedItem)

  if (editingIndex.value !== null) {
    if (editingIndex.value === fromIndex) {
      editingIndex.value = targetIndex
    } else if (fromIndex < targetIndex && editingIndex.value > fromIndex && editingIndex.value <= targetIndex) {
      editingIndex.value -= 1
    } else if (fromIndex > targetIndex && editingIndex.value >= targetIndex && editingIndex.value < fromIndex) {
      editingIndex.value += 1
    }
  }

  updateTypeCounter(copiedUsageItems)
  modelValue.value = copiedUsageItems
  emit('reorder', { fromIndex, toIndex: targetIndex })
}

function onDragEnd(): void {
  draggingIndex.value = null
  dragOverIndex.value = null
}

function updateTypeCounter(usageItems: UsageWithBuildingType[]): void {
  const typeCount = new Map<string, number>()
  usageItems.forEach((usage) => {
    usage.typeCounter = typeCount.get(usage.type) || 0
    typeCount.set(usage.type, usage.typeCounter + 1)
  })
}
</script>

<style scoped lang="postcss">
.selected-list {
  @apply overflow-visible border border-neutral-300 bg-white;
}

.item-card {
  @apply relative border-b border-neutral-300 last:border-b-0;
  @apply transition-all duration-150;
}

.item-card.is-editing {
  @apply z-[190];
}

.item-card.is-drop-target-before {
  @apply bg-neutral-35;
  margin-top: 0.5rem;
}

.item-card.is-drop-target-before::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: -0.5rem;
  height: 2px;
  background: var(--color-primary-dark);
}

.item-card.is-drop-target-after {
  @apply bg-neutral-35;
  margin-bottom: 0.5rem;
}

.item-card.is-drop-target-after::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -0.5rem;
  height: 2px;
  background: var(--color-primary-dark);
}

.item-card.is-dragging {
  @apply opacity-100;
}

.item-card.is-dragging .item-row {
  background: linear-gradient(
    180deg,
    rgba(251, 251, 251, 0) 0%,
    rgba(251, 251, 251, 0.18) 22%,
    rgba(251, 251, 251, 0.88) 42%,
    rgba(251, 251, 251, 0.88) 58%,
    rgba(251, 251, 251, 0.18) 78%,
    rgba(251, 251, 251, 0) 100%
  );
}

.item-row {
  @apply flex items-center justify-between gap-4 px-4 py-3;
}

.item-main {
  @apply flex min-w-0 flex-1 items-center gap-2;
}

.drag-handle {
  @apply flex cursor-grab items-center justify-center text-neutral-550;
}

.item-card.is-dragging .drag-handle {
  cursor: grabbing;
}

.category-reference-hint {
  @apply flex h-5 w-5 shrink-0 cursor-help items-center justify-center text-amber-600;
}

.action-button {
  @apply flex h-8 w-8 items-center justify-center rounded-none border border-transparent;
  @apply text-neutral-500 hover:text-primary-dark;
}

.action-button.is-active {
  @apply border-transparent bg-transparent text-primary-dark;
}

.remove-button {
  @apply hover:text-primary-dark;
}

.change-menu {
  @apply absolute top-full z-[210] mt-2 bg-white p-2;
  @apply border border-neutral-300;
  left: -10px;
  right: -10px;
  transform-origin: top center;
  animation: usage-menu-grow 100ms ease-out;
  box-shadow:
    0 4px 5px rgba(0, 0, 0, 0.13),
    0 10px 14px -6px rgba(0, 0, 0, 0.16);
}

@keyframes usage-menu-grow {
  from {
    opacity: 0;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
